/**
 * InlineRecordPanelSkeleton — the record panel's chrome, rendered as a
 * placeholder before `InlineCrmObjectPanel` has a record to show.
 *
 * There is no `SlideoutMenu` here, so this component IS the chrome
 * (`InlineCrmObjectPanel` precedent): it publishes `SurfaceDismissContext`
 * and renders `SlideoutMenu.Header` (which carries the real close button)
 * around a loading body — Escape/dismiss handling matches the real panel
 * because the same context contract is published, not because this
 * component reimplements it.
 */

import { type ReactElement, useMemo } from 'react';

import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import {
  SurfaceDismissContext,
  type SurfaceDismissContextValue,
} from '@/lib/base/overlays/surface-dismiss-context';
import {
  RecordBodySkeleton,
  RecordHeaderSkeleton,
} from '@/lib/crm/record/record-skeleton';

const HEADER_SLOT = <RecordHeaderSkeleton />;

export interface InlineRecordPanelSkeletonProps {
  /** Dismiss the placeholder. Required — the host decides what closing means. */
  onClose: () => void;
}

export function InlineRecordPanelSkeleton({
  onClose,
}: InlineRecordPanelSkeletonProps): ReactElement {
  const dismiss = useMemo<SurfaceDismissContextValue>(
    () => ({
      close: onClose,
      back: undefined,
      isEntering: false,
      isExiting: false,
    }),
    [onClose]
  );

  return (
    <SurfaceDismissContext value={dismiss}>
      <div
        className='flex h-full w-full flex-col'
        data-component='inline-record-panel-skeleton'
      >
        <SlideoutMenu.Header>{HEADER_SLOT}</SlideoutMenu.Header>
        <SlideoutMenu.Content className='px-0 md:px-0'>
          <div role='status'>
            <span className='sr-only'>Loading record…</span>
            <RecordBodySkeleton />
          </div>
        </SlideoutMenu.Content>
      </div>
    </SurfaceDismissContext>
  );
}
